import { resolveAddToCartAvailability } from '@/utils/product/add-to-cart-availability';
import { isBackorderRestricted } from '@/utils/isBackorderRestricted';

// EWM-944 — whether an uploaded order line (file uploader OR Quick Order) is
// accepted, and whether it will back-order.
//
// The uploader previously rejected EVERY out-of-stock line regardless of the
// customer's entitlement, so a back-order-allowed customer had to hand-strip
// lines they were entitled to order. This delegates to the EWM-360 rules that
// already drive the PDP/PLP Add-to-Cart button, so the uploader and the product
// pages can never disagree about what a customer may back-order.
//
// Acceptance here is NOT a commitment (AC 7): stock and entitlement are
// re-evaluated by the BFF `checkout` gate under the normal rules.

export type UploadLineRejection = 'out-of-stock' | 'excluded-from-back-order' | 'removed-from-sale';

export type UploadLineAvailability =
  { accepted: true; backOrder: boolean } | { accepted: false; reason: UploadLineRejection };

export interface UploadLineAvailabilityInput {
  /** Resolver stock signal for the SKU. `undefined` ⇒ treated as out of stock. */
  inStock?: boolean;
  /** Lifecycle `GO` (removed from sale) — excluded from back order for everyone. */
  removedFromSale?: boolean;
  /**
   * BusinessUnit `back-orders-allowed`, read from the BU's OWN custom field.
   * Parent inheritance is unverified — nothing walks `parentUnit`.
   */
  backOrdersAllowed?: boolean;
  /** BusinessUnit `payment-terms`. "P" = cash account, which cannot back-order. */
  paymentTerms?: string;
  /** Quantity the uploaded row asked for. */
  requestedQuantity?: number;
  /** Stock the resolver reported, when it knows. */
  availableQuantity?: number;
}

export const resolveUploadLineAvailability = ({
  inStock,
  removedFromSale = false,
  backOrdersAllowed = false,
  paymentTerms,
  requestedQuantity,
  availableQuantity,
}: UploadLineAvailabilityInput): UploadLineAvailability => {
  // Entitlement is the FULL rule the checkout gate enforces, not just
  // `back-orders-allowed`. A cash account (`payment-terms` = "P") cannot
  // back-order even when its BU allows it — reading the flag alone would accept
  // a whole uploaded file, badge every line "Back Order", and then strand the
  // customer on a disabled Proceed button at checkout. `isBackorderRestricted`
  // is the FE mirror of the authoritative BFF rule (EWM-641); reusing it is
  // what keeps the uploader and the gate from drifting.
  const entitled = !isBackorderRestricted({ backOrdersAllowed, paymentTerms });

  // "In stock but fewer units than the row asked for."
  //
  // This narrows the BADGE only, never acceptance. An earlier revision fed
  // `inStock && !short` into the shared gate, which silently made the uploader
  // stricter than it had ever been for customers WITHOUT the entitlement: a
  // short in-stock row used to be accepted on `stockStatus === 'in-stock'`
  // alone and started being rejected as out-of-stock. No AC asked for that, it
  // was stricter than the PDP (which has no quantity gate at all), and the BFF
  // checkout gate already catches a genuine shortfall. So acceptance is decided
  // on the real stock signal and `short` only decides whether an ENTITLED
  // customer's accepted line is flagged as a back-order.
  const short =
    typeof requestedQuantity === 'number' &&
    typeof availableQuantity === 'number' &&
    requestedQuantity > availableQuantity;

  const { isBackOrder, disabled } = resolveAddToCartAvailability({
    inStock,
    removedFromSale,
    backOrdersAllowed: entitled,
  });

  if (!disabled) {
    // AC 10 — the whole line back-orders; the quantity is never trimmed to
    // available stock.
    return { accepted: true, backOrder: isBackOrder || (entitled && short) };
  }

  // `removedFromSale` is reported ahead of a bare stock rejection so the
  // customer can tell a permanently-excluded SKU from one that is simply out of
  // stock right now (AC 6).
  //
  // WHY THIS BRANCH LOOKS DEAD — and why it stays. It is unreachable through the
  // uploader while the catalogue is single-variant, which it is today.
  //
  // The BFF filters GO products out before the frontend sees them
  // (`ProductApi.query` -> `filterProductsForSite`, EWM-643), so the ORDINARY
  // single-variant takedown never reaches an uploaded row: that SKU falls to
  // the unmatched branch and reads the drawer's DEFAULT unmatched-SKU message,
  // "SKU not found" (`quick-order.sku-not-found`, rendered by
  // `components/organisms/quick-order-upload` and its `unmatched-row`).
  //
  // EWM-949 note: since that ticket "SKU not found" is the FALLBACK the row
  // shows absent a per-line reason (`unmatched-row` renders
  // `{reason ?? 'SKU not found'}`), and the uploader has its own
  // `removed-from-sale` rejection reason. That does NOT contradict the decision
  // below — the removed-from-sale path stays on the fallback while the
  // catalogue is single-variant; see the SETTLED block.
  //
  // The branch exists because the two sides work at different granularities.
  // `isProductRemovedFromSaleForSite` asks `lifecycleCodeForSite`, which returns
  // the code from the FIRST variant carrying a parseable entry for the site — a
  // PRODUCT-level answer. `resolveSkus` derives `removedFromSale` PER VARIANT.
  // So a multi-variant product whose first variant is not GO would pass the BFF
  // filter, and an uploaded SKU matching one of its GO variants would arrive
  // here with `removedFromSale: true`. No such product existed when this was
  // measured against `electus-dev` on 2026-09-17. The live count is the `note`
  // on `ct.product.single-variant` in the latest conformity run, not a figure
  // frozen in this comment.
  //
  // SETTLED — EWM-998. Luke Greenfield (PO), 2026-09-11: "Accept item not
  // located as the error message. Note that all products have only one variant
  // so both paths end up functioning the same." EWM-944 AC 6 is closed by that
  // decision rather than left open: the DEFAULT unmatched-SKU message is the
  // intended outcome for an uploaded removed-from-sale SKU, and EWM-998 does not
  // build a special distinguishable message for that case.
  //
  // How this squares with EWM-949's per-line reasons: that ticket does define a
  // `removed-from-sale` reason, but it can only fire on a row that reached
  // `resolveSkus` and came back `removedFromSale: true` — which, as the
  // granularity note above explains, requires a MULTI-VARIANT product (a GO SKU
  // on a single-variant product is filtered out by the BFF and never reaches
  // the uploader). So while the single-variant premise holds, a removed-from-sale
  // SKU still reads the fallback, exactly as Luke's decision intends; EWM-949's
  // reason only becomes the message for it if that premise breaks — the very
  // drift `ct.product.single-variant` watches for. The decision and EWM-949 are
  // complementary, not in conflict.
  //
  // MIND THE STRING the decision names. "Item not located"
  // (`quick-order.item-not-located`) is never shown to a dealer: its only
  // render site is
  // `components/organisms/quick-order/quick-order-desktop/quick-order-csv-upload`,
  // whose sole importer is its own context provider, so it is never mounted
  // (EWM-944 AC 8; mounting it is tracked as EWM-989). The drawer that ships
  // says "SKU not found". The wrong string travelled from EWM-944's AC 6 row
  // into this ticket's title and into Luke's wording; the SUBSTANCE of his
  // decision — accept the generic not-found message rather than build a
  // distinguishable one — transfers to "SKU not found" unchanged, which is why
  // this is a correction to the record and not a re-opened question.
  //
  // The premise is MONITORED, not merely asserted here — conformity invariant
  // `ct.product.single-variant`
  // (packages/electus/migration/src/conformity/invariants/ct.ts) re-counts
  // multi-variant products on every conformity run. Know its reach before
  // leaning on it: it is `warn` severity, so a hit prints a warning line with
  // its offenders while the report still ends `RESULT: PASS` and leaves the
  // run's exit code untouched — the CI conformity job and every release gate
  // that reads `exit=$?` pass exactly as before. Nothing automated catches it;
  // a human reading the per-invariant lines does, and four places ask for that
  // read: `/conformity-check`, which `/ship-pr` invokes for any PR whose
  // dossier lists a data assumption, and the conformity gates in
  // `.claude/commands/release-to-qa.md`, `release-to-staging.md` and
  // `release-to-production.md`. (`hotfix-to-prod.md` is the one that reads the
  // exit code alone.) So a hit surfaces at PR time and at every promotion — to
  // a reader, not to a gate. If that warning ever appears, THAT is the trigger
  // to re-open the granularity question and this comment is stale.
  //
  // Two REJECTION REASONS (the `reason` values returned below), because one
  // message cannot honestly cover both cases.
  //
  // OUT OF STOCK + GO: the customer might reasonably have expected a
  // back-order, so naming back-order is the useful thing to say.
  // IN STOCK + GO: back-order has nothing to do with it. Telling someone an
  // item sitting on the shelf "cannot be back-ordered" explains nothing and
  // reads as a bug. It is simply no longer sold.
  //
  // The in-stock case is also a BEHAVIOUR CHANGE: before this ticket the
  // uploader looked only at `stockStatus`, so an in-stock GO row was accepted
  // for everyone. Keeping the rejection aligns the uploader with the PDP and
  // with `CartApi`, which strips GO line items region-scoped — previously the
  // row was accepted at upload and then silently dropped from the cart.
  if (removedFromSale) {
    return { accepted: false, reason: inStock ? 'removed-from-sale' : 'excluded-from-back-order' };
  }
  return { accepted: false, reason: 'out-of-stock' };
};
